import { useEffect, useState } from 'react';

import VectorIcon from 'assets/VectorIcon.svg';
import ColorPicker from 'common-ui/colorPicker';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import { Select } from 'common-ui/select';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import * as Yup from 'yup';

import {
  ErrorText,
  ErrorTextStatic,
  FormContainer,
  SwitchWrapper,
} from './WorkflowStatusModal.styles';
import { COLOR_OPTIONS, OUTCOME_TYPE_OPTIONS } from './constants';

const validationSchema = Yup.object({
  name: Yup.string()
    .transform((v) => (v ? v.trim() : ''))
    .required('Name is required')
    .max(50, 'Max 50 characters'),

  color: Yup.string().required('Color is required'),

  description: Yup.string().max(250),

  outcomeType: Yup.object().required(),

  enabled: Yup.boolean(),
});

const ErrorWithIcon = ({ message }) => {
  if (!message) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <img src={VectorIcon} alt="error" width={14} height={14} />
      <span>{message}</span>
    </div>
  );
};

const WorkflowStatusModal = ({
  mode = 'create',
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  initialData,
}) => {
  const isEditMode = mode === 'edit';

  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#E63946',
    outcomeType: OUTCOME_TYPE_OPTIONS[0],
    enabled: true,
  });

  useEffect(() => {
    if (isOpen && isEditMode && initialData) {
      const selectedOutcomeType =
        OUTCOME_TYPE_OPTIONS.find((o) => o.value === initialData.outcomeType) ||
        OUTCOME_TYPE_OPTIONS[0];

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        color: initialData.color || '#E63946',
        outcomeType: selectedOutcomeType,
        enabled: initialData.enabled ?? true,
      });
    }
    if (isOpen && !isEditMode) {
      setFormData({
        name: '',
        description: '',
        color: '#E63946',
        outcomeType: OUTCOME_TYPE_OPTIONS[0],
        enabled: true,
      });
    }
    setErrors({});
  }, [isOpen, isEditMode, initialData]);

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      color: '#E63946',
      outcomeType: OUTCOME_TYPE_OPTIONS[0],
      enabled: true,
    });
    setErrors({});
    onClose();
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleColorClick = (color) => {
    setFormData((prev) => ({
      ...prev,
      color,
    }));
    setErrors((prev) => ({
      ...prev,
      color: '',
    }));
  };

  const handleOutcomeTypeChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      outcomeType: selectedOption,
    }));
  };

  const handleSwitchToggle = () => {
    setFormData((prev) => ({
      ...prev,
      enabled: !prev.enabled,
    }));
    setErrors((prev) => ({
      ...prev,
      enabled: '',
    }));
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, {
        abortEarly: false,
      });

      const dataToSend = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color,
        enabled: formData.enabled,
      };

      if (!initialData?.isDefault) {
        dataToSend.outcomeType = formData.outcomeType.value;
      }
      if (isEditMode) {
        await onUpdate(initialData.id, dataToSend);
      } else {
        await onCreate(dataToSend);
      }

      handleClose();
    } catch (err) {
      if (err.inner) {
        const mappedErrors = {};

        err.inner.forEach((e) => {
          if (!mappedErrors[e.path]) {
            mappedErrors[e.path] = e.message;
          }
        });

        setErrors(mappedErrors);
      } else {
        // Handle API errors (from the update/create actions)
        const errorMessage =
          typeof err === 'string' ? err : err.message || 'An error occurred';

        // If the error message contains "Cannot disable workflow status", show it on the enabled field
        if (
          errorMessage.includes('Cannot disable workflow status') ||
          errorMessage.includes('currently assigned to')
        ) {
          setErrors((prev) => ({
            ...prev,
            enabled: errorMessage,
          }));
        } else {
          // For other API errors, you might want to show them differently
          setErrors((prev) => ({
            ...prev,
            enabled: errorMessage,
          }));
        }
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={isEditMode ? 'Edit Workflow Status' : 'Create Workflow Status'}
      footer
      closeIcon
      okText={isEditMode ? 'Update' : 'Create'}
      cancelText="Cancel"
    >
      <FormContainer>
        <div style={{ position: 'relative' }}>
          <Input
            label="Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            placeholder="Enter status name"
            required
            maxLength={50}
            error={!!errors.name}
          />
          {errors.name && (
            <ErrorText>
              <ErrorWithIcon message={errors.name} />
            </ErrorText>
          )}
        </div>

        <TextArea
          label="Description (Optional)"
          value={formData.description}
          onChange={handleInputChange('description')}
          placeholder="Enter description"
          rows={3}
          maxLength={250}
        />

        <Select
          label="Outcome Type"
          value={formData.outcomeType}
          onChange={handleOutcomeTypeChange}
          options={OUTCOME_TYPE_OPTIONS}
          isClearable={false}
          isDisabled
        />

        <div>
          <ColorPicker
            label="Select status color"
            colorOptions={COLOR_OPTIONS}
            selectedColor={formData.color}
            handleColorClick={handleColorClick}
          />
          {errors.color && (
            <ErrorText>
              <ErrorWithIcon message={errors.color} />
            </ErrorText>
          )}
        </div>

        <div>
          <SwitchWrapper>
            <Switch isOn={formData.enabled} onToggle={handleSwitchToggle} />
            {formData.enabled ? 'Enabled' : 'Disabled'}
          </SwitchWrapper>
          {errors.enabled && (
            <ErrorTextStatic>
              <ErrorWithIcon message={errors.enabled} />
            </ErrorTextStatic>
          )}
        </div>
      </FormContainer>
    </Modal>
  );
};

export default WorkflowStatusModal;
